export const CARE_DATE_MONTH_NAME_PATTERN = '(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)';
export const CARE_DATE_WEEKDAY_NAME_PATTERN = '(?:mon(?:day)?|tue(?:s(?:day)?)?|wed(?:nesday)?|thu(?:rs(?:day)?)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)';
export const CARE_DATE_WEEKDAY_PREFIX_PATTERN = `(?:(?:${CARE_DATE_WEEKDAY_NAME_PATTERN})(?:,)?\\s+)?`;

export const CARE_TIME_PREFIX_PATTERN = String.raw`\b(?:around|about|at|approx(?:\.|imately)?|by|during|near|before|after|closer\s+to)\b\s*`;
export const CARE_TIME_24_HOUR_PATTERN = String.raw`(?:[01]?\d|2[0-3])[:.][0-5]\d`;
export const CARE_TIME_MERIDIEM_PATTERN = String.raw`(?:1[0-2]|0?[1-9])(?:[:.][0-5]\d)?\s*[AaPp]\.?[Mm]\.?`;
export const CARE_TIME_CLOCK_PATTERN = String.raw`(?:[01]?\d|2[0-3])[:.][0-5]\d(?:\s*[AaPp]\.?[Mm]\.?)?`;
export const CARE_TIME_SHORT_PATTERN = String.raw`(?:${CARE_TIME_CLOCK_PATTERN}|(?:1[0-2]|0?[1-9])(?:\s*[AaPp]\.?[Mm]\.?)?)`;
export const CARE_EXPLICIT_TIME_PATTERN = `(?:${CARE_TIME_MERIDIEM_PATTERN}|${CARE_TIME_24_HOUR_PATTERN})`;
export const CARE_OPTIONAL_EXPLICIT_TIME_PATTERN = `(?:,?\\s+${CARE_EXPLICIT_TIME_PATTERN})?`;
export const CARE_TIME_RANGE_VALUE_PATTERN = String.raw`(?:${CARE_TIME_CLOCK_PATTERN}|(?:1[0-2]|0?[1-9])(?:[:.][0-5]\d)?(?:\s*[AaPp]\.?[Mm]\.?)?)`;

export const CARE_TIME_REJECT_BARE_HOUR_AFTER_PATTERN = /^\s*(?:[-–—]\s*\d|\d|\b(?:minute|minutes|mins?|min|mg|ml|units?)\b)/i;
export const CARE_TIME_REJECT_BARE_HOUR_BEFORE_PATTERN = /\b(?:level|grade|room|dose|mg|ml|mcg|unit|units)\s*$/i;

export const CARE_TIME_CLOCK_REGEX = new RegExp(`(?:${CARE_TIME_PREFIX_PATTERN})?(?<time>\\b${CARE_TIME_CLOCK_PATTERN}\\b)`, 'ig');
export const CARE_TIME_SHORT_REGEX = new RegExp(`(?:${CARE_TIME_PREFIX_PATTERN})?(?<time>\\b${CARE_TIME_SHORT_PATTERN}\\b)`, 'ig');
export const CARE_EXPLICIT_TIME_REGEX = new RegExp(`\\b${CARE_EXPLICIT_TIME_PATTERN}\\b`, 'i');
export const CARE_EXPLICIT_TIME_GLOBAL_REGEX = new RegExp(`\\b${CARE_EXPLICIT_TIME_PATTERN}\\b`, 'ig');
export const CARE_TIME_RANGE_SENTENCE_REGEX = new RegExp(`\\b(?:from|between)\\b[\\s\\S]{0,48}\\b(?:${CARE_TIME_RANGE_VALUE_PATTERN})\\b[\\s\\S]{0,24}\\b(?:to|and)\\b[\\s\\S]{0,24}\\b(?:${CARE_TIME_RANGE_VALUE_PATTERN})\\b`, 'i');
export const CARE_TIME_SHIFT_REGEX = new RegExp(`\\b(?:at\\s+${CARE_TIME_SHORT_PATTERN}|this morning|this afternoon|this evening|tonight|today|after school|before bedtime)\\b`, 'gi');

export const CARE_DATE_MONTH_DAY_PATTERN = `${CARE_DATE_MONTH_NAME_PATTERN}\\s+\\d{1,2}(?:st|nd|rd|th)?`;
export const CARE_DATE_MONTH_DAY_WITH_OPTIONAL_TIME_PATTERN = `${CARE_DATE_WEEKDAY_PREFIX_PATTERN}${CARE_DATE_MONTH_DAY_PATTERN}(?:,)?(?:\\s+\\d{4})?${CARE_OPTIONAL_EXPLICIT_TIME_PATTERN}`;
export const CARE_DATE_MONTH_DAY_WITH_REQUIRED_TIME_PATTERN = `${CARE_DATE_WEEKDAY_PREFIX_PATTERN}${CARE_DATE_MONTH_DAY_PATTERN}(?:,)?\\s+${CARE_EXPLICIT_TIME_PATTERN}`;

export const createCareTimelineHeadingRegex = ({
  global = false,
  requireTime = false,
  requireWeekday = false,
  start = false,
} = {}) => {
  const weekdayPattern = requireWeekday
    ? `${CARE_DATE_WEEKDAY_NAME_PATTERN}(?:,)?\\s+`
    : CARE_DATE_WEEKDAY_PREFIX_PATTERN;
  const timePattern = requireTime
    ? `(?:,)?\\s+${CARE_EXPLICIT_TIME_PATTERN}`
    : `(?:,)?(?:\\s+\\d{4})?${CARE_OPTIONAL_EXPLICIT_TIME_PATTERN}`;
  const pattern = `${weekdayPattern}${CARE_DATE_MONTH_DAY_PATTERN}${timePattern}`;
  const prefix = start ? '^' : '\\b';
  return new RegExp(`${prefix}${pattern}\\b`, global ? 'ig' : 'i');
};

export const normalizeCareTimeMeridiemText = (value = '') => String(value || '')
  .replace(/(\d)([AaPp])([Mm])\b/g, '$1 $2$3')
  .replace(/\b([AaPp])\s*\.?\s*([Mm])\.?(?=\s|$)/g, '$1$2')
  .replace(/\b([AaPp])([Mm])\.(?=\s|$)/g, '$1$2')
  .replace(/\b([AaPp])([Mm])\b/g, (_, meridiem, m) => `${meridiem.toUpperCase()}${m.toUpperCase()}`);

export const stripTrailingCareTimePunctuation = (value = '') => String(value || '')
  .replace(new RegExp(`(\\b${CARE_TIME_MERIDIEM_PATTERN})\\.(?=\\s|$)`, 'g'), '$1')
  .replace(new RegExp(`(\\b${CARE_TIME_24_HOUR_PATTERN})\\.(?=\\s|$)`, 'g'), '$1')
  .replace(new RegExp(`(\\b${CARE_TIME_MERIDIEM_PATTERN})\\s*(?:--?|–|—)\\s*$`, 'g'), '$1')
  .replace(new RegExp(`(\\b${CARE_TIME_24_HOUR_PATTERN})\\s*(?:--?|–|—)\\s*$`, 'g'), '$1');

export const normalizeCareDateTimeText = (value = '') => stripTrailingCareTimePunctuation(
  normalizeCareTimeMeridiemText(value)
).replace(/\s+/g, ' ').trim();

export const normalizeCareTimeLabel = (value = '') => normalizeCareTimeMeridiemText(value)
  .replace(/(\d)\.(\d{2})/, '$1:$2')
  .replace(/\s+/g, ' ')
  .trim();

export const normalizeCareTimeCandidate = (candidateText = '', {
  contextText = '',
  inferMeridiemFromContext = null,
} = {}) => {
  const trimmedCandidate = normalizeCareTimeLabel(candidateText);
  if (!trimmedCandidate) return null;

  const match = trimmedCandidate.match(/^(\d{1,2})(?:[:](\d{2}))?(?:\s*([AaPp][Mm]))?$/);
  if (!match) return null;

  let hour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const explicitAmPm = String(match[3] || '').toLowerCase();
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (minute < 0 || minute > 59) return null;
  if (explicitAmPm && (hour < 1 || hour > 12)) return null;
  if (!explicitAmPm && (hour < 0 || hour > 23)) return null;

  const meridiem = explicitAmPm
    || (
      hour >= 1 && hour <= 12 && typeof inferMeridiemFromContext === 'function'
        ? inferMeridiemFromContext(contextText || trimmedCandidate, hour)
        : ''
    );
  if (!explicitAmPm && hour >= 1 && hour <= 12 && !meridiem) {
    return null;
  }
  if (meridiem.startsWith('p') && hour < 12) hour += 12;
  else if (meridiem.startsWith('a') && hour === 12) hour = 0;

  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

export const extractCareTimeFromText = (text = '', {
  inferMeridiemFromContext = null,
  rejectBareHourAfterPattern = CARE_TIME_REJECT_BARE_HOUR_AFTER_PATTERN,
  rejectBareHourBeforePattern = CARE_TIME_REJECT_BARE_HOUR_BEFORE_PATTERN,
} = {}) => {
  const sourceText = String(text || '');
  let matches = Array.from(sourceText.matchAll(CARE_TIME_CLOCK_REGEX));
  if (!matches.length) matches = Array.from(sourceText.matchAll(CARE_TIME_SHORT_REGEX));
  const firstMatch = matches[0];
  if (!firstMatch?.groups?.time) return { time: null, matchedText: null, cleanedText: sourceText.trim() };

  const matchedText = firstMatch[0] || firstMatch.groups.time || '';
  const candidate = String(firstMatch.groups.time || '').trim();
  const hasExplicitMeridiem = /\b(?:[ap]\.?m\.?)\b/i.test(matchedText);
  const hasClockFormat = /:/.test(candidate) || /\./.test(candidate);
  if (!hasExplicitMeridiem && !hasClockFormat) {
    const matchIndex = typeof firstMatch.index === 'number' ? firstMatch.index : sourceText.indexOf(matchedText);
    if (matchIndex >= 0) {
      const before = sourceText.slice(Math.max(0, matchIndex - 24), matchIndex);
      if (rejectBareHourBeforePattern?.test?.(before)) {
        return { time: null, matchedText: null, cleanedText: sourceText.trim() };
      }
      const after = sourceText.slice(matchIndex + matchedText.length, matchIndex + matchedText.length + 24);
      if (rejectBareHourAfterPattern?.test?.(after)) {
        return { time: null, matchedText: null, cleanedText: sourceText.trim() };
      }
    }
  }

  const time = normalizeCareTimeCandidate(candidate, {
    contextText: sourceText,
    inferMeridiemFromContext,
  });
  if (!time) return { time: null, matchedText: null, cleanedText: sourceText.trim() };

  return {
    time,
    matchedText: firstMatch[0] || firstMatch.groups.time || null,
    cleanedText: sourceText.replace(firstMatch[0] || firstMatch.groups.time, ' ').replace(/\s+/g, ' ').trim(),
  };
};

export const countCareExplicitTimes = (text = '') => Array.from(String(text || '').matchAll(CARE_EXPLICIT_TIME_GLOBAL_REGEX)).length;
export const hasCareExplicitTime = (text = '') => CARE_EXPLICIT_TIME_REGEX.test(String(text || ''));
